import { get } from "./request";
import request from "./request";

/**
 * 订单管理接口（API 文档第 11 节 /console/orders）。
 *
 * 列表响应为下游透传，实际结构形如：
 *   { "orders": [ { id, status, partnerOrderId, placedAt, cart:{...}, ... } ], "total": 10 }
 * 其中：
 *   - id                        → BXL 订单号
 *   - partnerOrderId            → 商家订单号
 *   - status / statusHuman      → 状态枚举 / 中文文案
 *   - placedAt (epoch 毫秒)      → 下单时间
 *   - merchantOrderPlacedAt      → 商家接单时间（0 表示尚未接单）
 *   - cart.grandTotal (分)       → 实际总额（含税）
 *   - Order Price 取 cart.groups[0].totalCostCents（回落 cart.grandTotal）
 *   - 包裹号/承运商取自 cart.groups[0].items[].shipping[].packages[]
 *     （第一个 item 的最新一条物流，取不到则往后找）
 * 响应不含 page/size。
 */

// ---------- 查询参数 ----------

export interface OrdersQuery {
  /** 页码，默认 1 */
  page?: number;
  /** 每页条数，默认 20 */
  size?: number;
  /** BXL Order ID 搜索（文档中的 search 参数） */
  search?: string;
  /** Merchant Order ID 搜索（网关透传的附加筛选） */
  merchantOrderId?: string;
  /** 状态筛选（status 枚举值） */
  status?: string;
  /** Created At 范围起（ISO instant） */
  from?: string;
  /** Created At 范围止（ISO instant） */
  to?: string;
}

// ---------- 归一化后的类型 ----------

/** 归一化后的订单行，字段与设计稿的列一一对应 */
export interface OriginalOrder {
  /** BXL Order ID（order.id） */
  orderId: string;
  /** Merchant Order ID（cart.groups[0].merchantOrderId，可能缺失） */
  merchantOrderId: string | null;
  /** 下单时间 Created At（ISO；由 placedAt 毫秒转换） */
  createdAt: string | null;
  /** 商家接单时间 Received At（ISO；由 merchantOrderPlacedAt 转换，0 时为空） */
  receivedAt: string | null;
  /** 实际扣款金额 Actual Amount（分；订单最外层 merchantOrderTotalCents） */
  actualAmountCents: number | null;
  /** 订单状态枚举（如 AWAIT_CONCIERGE） */
  status: string;
  /** 商家标价 Order Price（分；groups[0].totalCostCents，回落 cart.grandTotal） */
  orderPriceCents: number | null;
  /** 包裹号 Package No.（第一个 item 最新物流的 trackingNumber，取不到往后找） */
  packageNo: string | null;
  /** 承运商 Carrier（第一个 item 最新物流的 carrier，取不到往后找） */
  carrier: string | null;
}

export interface OrdersPage {
  orders: OriginalOrder[];
  total: number;
  page: number;
  size: number;
}

// ---------- 订单详情（弹窗）归一化类型 ----------
// 数据取自 /orders/{orderId}：
//   - 商品明细取 cart.groups[0].items（固定 index 0）
//   - 商品信息在 item.sku
//   - item.shipping[] 为「发货/包裹」列表：同一 SKU 买多件被拆成多个包裹时会有多条
//   - shipping[i].packages[] 为同一包裹的「多段承运」（不同物流公司/单号），
//     其 progresses 需要聚合成一条完整轨迹展示

/** 物流轨迹节点 */
export interface OrderPackageProgress {
  /** 节点时间（ISO） */
  timestamp: string | null;
  /** 节点地点 */
  location: string | null;
  /** 节点描述 */
  activity: string | null;
}

/**
 * 一段物流（对应设计稿里的一个 "Shipment"）。
 * 来源为 shipping[i].packages[j]：每段有自己的包裹号与独立的 progresses 轨迹，
 * 各段之间不做合并，逐段展示。
 */
export interface OrderShipmentSegment {
  /** 运单号 */
  trackingNumber: string | null;
  /** 承运商代码，如 "STO" / "AMAZON" */
  carrier: string | null;
  /** 承运商名称，如 "快递STO" */
  carrierName: string | null;
  /** 物流类型名称，如 "国内物流" / "美国境内物流" */
  kindName: string | null;
  /** 物流状态枚举，如 "EN_ROUTE" / "DELIVERED" */
  status: string | null;
  /** 预计时效文案 */
  estimatedDuration: string | null;
  /** 最近更新时间（ISO） */
  lastUpdatedAt: string | null;
  /** 是否已签收（status 为 DELIVERED） */
  delivered: boolean;
  /** 本段轨迹节点（按时间升序，最新在末尾） */
  progresses: OrderPackageProgress[];
}

/** 订单详情中的单个商品行（对应弹窗 Items 区每一张卡片） */
export interface OrderDetailItem {
  /** 商品缩略图 URL（sku.images[0]，暂无则为 null，展示占位色块） */
  imageUrl: string | null;
  /** 商品标题（sku.name） */
  name: string;
  /** 品牌（sku.brand） */
  brand: string | null;
  /** 规格文案（sku.size.displayName / name） */
  size: string | null;
  /** 颜色（sku.color.name） */
  color: string | null;
  /** 数量（弹窗右上角 × N） */
  quantity: number;
  /**
   * 物流分段列表：由 item.shipping[].packages[] 平铺而来。
   * 逐段独立展示（Shipment 1 of N …），每段一个包裹号与一条轨迹。
   */
  segments: OrderShipmentSegment[];
}

/** 归一化后的订单详情，字段与弹窗各区块一一对应 */
export interface OrderDetail {
  /** BXL Order ID */
  orderId: string;
  /** Merchant Order ID（cart.groups[0].merchantOrderId） */
  merchantOrderId: string | null;
  /** Created At（ISO；由 placedAt 转换） */
  createdAt: string | null;
  /** 订单状态枚举 */
  status: string;
  /** 订单状态中文文案（statusHuman） */
  statusHuman: string | null;
  /** Order Price（分）—— 商家标价合计 */
  orderPriceCents: number | null;
  /** Actual Amount（分）—— 实际扣款 */
  actualAmountCents: number | null;
  /** 商品明细（cart.groups[0].items） */
  items: OrderDetailItem[];
}

// ---------- 下游原始结构（仅声明用到的字段） ----------

interface RawCartGroupLayout {
  merchandiseOriginalValueCents?: number;
  merchandiseCurrentValueCents?: number;
}

interface RawCartGroup {
  totalCostCents?: number;
  layout?: RawCartGroupLayout;
  items?: RawGroupItem[];
  merchantOrderId?: string;
}

interface RawCart {
  grandTotal?: number;
  originalGrandTotal?: number;
  groups?: RawCartGroup[];
}

interface RawOrder {
  id?: string;
  status?: string;
  statusHuman?: string;
  partnerOrderId?: string;
  placedAt?: number;
  merchantOrderPlacedAt?: number;
  expectedTotal?: number;
  merchantOrderTotalCents?: number;
  cart?: RawCart;
}

interface RawOrdersResponse {
  orders?: RawOrder[];
  total?: number;
}

// ---------- 归一化辅助 ----------

/** epoch 毫秒 → ISO 字符串；0 / 缺失 / 非法时返回 null */
function epochToIso(ms?: number): string | null {
  if (!ms || ms <= 0) return null;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** 商家标价 Order Price（分）：groups[0].totalCostCents → cart.grandTotal → 0 */
function orderPriceCents(cart?: RawCart): number {
  return cart?.groups?.[0]?.totalCostCents || cart?.grandTotal || 0;
}

/** Merchant Order ID：取自 cart.groups[0].merchantOrderId */
function groupMerchantOrderId(cart?: RawCart): string | null {
  return cart?.groups?.[0]?.merchantOrderId || null;
}

/** 实际扣款金额 Actual Amount（分）：取订单最外层 merchantOrderTotalCents */
function actualAmountCents(o: RawOrder): number | null {
  const cents = o.merchantOrderTotalCents;
  return typeof cents === "number" && Number.isFinite(cents) ? cents : null;
}

/** 包裹的时间戳（用于取“最新”）：lastUpdatedTime → createdAt → 最新轨迹节点时间 */
function packageTime(p: RawPackage): number {
  if (typeof p.lastUpdatedTime === "number" && p.lastUpdatedTime > 0) {
    return p.lastUpdatedTime;
  }
  if (typeof p.createdAt === "number" && p.createdAt > 0) return p.createdAt;
  const times = (Array.isArray(p.progresses) ? p.progresses : [])
    .map((pr) => (typeof pr.timestamp === "number" ? pr.timestamp : 0))
    .filter((t) => t > 0);
  return times.length ? Math.max(...times) : 0;
}

/** 取某个 item 下「最新一条物流」的包裹（跨 shipping[].packages[]，按更新时间取最新） */
function latestPackageOf(item: RawGroupItem): RawPackage | null {
  const packages = (Array.isArray(item.shipping) ? item.shipping : []).flatMap(
    (s) => (Array.isArray(s.packages) ? s.packages : [])
  );
  if (packages.length === 0) return null;
  return packages.reduce((best, p) =>
    packageTime(p) >= packageTime(best) ? p : best
  );
}

/**
 * 订单列表的 Package No. / Carrier：
 * 取第一个 item 的最新一条物流（trackingNumber → 包裹号，carrier → 承运商）；
 * 第一个 item 没有物流则依次往后找。
 */
function listShipmentInfo(o: RawOrder): {
  packageNo: string | null;
  carrier: string | null;
} {
  const items = o.cart?.groups?.[0]?.items;
  if (Array.isArray(items)) {
    for (const it of items) {
      const pkg = latestPackageOf(it);
      if (pkg) {
        return {
          packageNo: pkg.trackingNumber || null,
          carrier: pkg.carrier || null,
        };
      }
    }
  }
  return { packageNo: null, carrier: null };
}

/** 把下游透传的单条订单归一化成 OriginalOrder */
function normalizeOrder(o: RawOrder): OriginalOrder {
  const { packageNo, carrier } = listShipmentInfo(o);
  return {
    orderId: o.id ?? "",
    merchantOrderId: groupMerchantOrderId(o.cart),
    createdAt: epochToIso(o.placedAt),
    receivedAt: epochToIso(o.merchantOrderPlacedAt),
    actualAmountCents: actualAmountCents(o),
    status: o.status ?? "",
    orderPriceCents: orderPriceCents(o.cart),
    packageNo,
    carrier,
  };
}

// ---------- 订单详情：下游原始结构（仅声明用到的字段） ----------

interface RawSkuImageVariant {
  url?: string;
}

interface RawSkuImage {
  thumbnail?: RawSkuImageVariant;
  full?: RawSkuImageVariant;
}

interface RawSkuSize {
  name?: string;
  displayName?: string;
  label?: string;
}

interface RawSkuColor {
  name?: string;
}

interface RawSku {
  name?: string;
  brand?: string;
  size?: RawSkuSize;
  color?: RawSkuColor;
  images?: RawSkuImage[];
}

interface RawProgress {
  timestamp?: number;
  location?: string;
  activity?: string;
}

interface RawPackage {
  trackingNumber?: string;
  carrier?: string;
  carrierName?: string;
  kind?: string;
  kindName?: string;
  status?: string;
  estimatedDuration?: string;
  createdAt?: number;
  lastUpdatedTime?: number;
  progresses?: RawProgress[];
}

interface RawShipping {
  packages?: RawPackage[];
}

interface RawGroupItem {
  quantity?: number;
  cents?: number;
  sku?: RawSku;
  shipping?: RawShipping[];
}

// 订单详情响应与列表单条同构，额外用到 cart.groups[].items
type RawOrderDetailResponse = RawOrder;

/** 归一化单个轨迹节点 */
function normalizeProgress(pr: RawProgress): OrderPackageProgress {
  return {
    timestamp: epochToIso(pr.timestamp),
    location: pr.location || null,
    activity: pr.activity || null,
  };
}

/** 归一化单段物流（shipping[i].packages[j]）：保留自身独立轨迹 */
function normalizeSegment(p: RawPackage): OrderShipmentSegment {
  const progresses = (Array.isArray(p.progresses) ? p.progresses : [])
    .map(normalizeProgress)
    .map((pr, idx) => ({ pr, idx }))
    .sort((a, b) => {
      const ta = a.pr.timestamp;
      const tb = b.pr.timestamp;
      if (ta && tb) return ta < tb ? -1 : ta > tb ? 1 : a.idx - b.idx;
      if (ta) return -1;
      if (tb) return 1;
      return a.idx - b.idx;
    })
    .map(({ pr }) => pr);
  return {
    trackingNumber: p.trackingNumber || null,
    carrier: p.carrier || null,
    carrierName: p.carrierName || null,
    kindName: p.kindName || null,
    status: p.status || null,
    estimatedDuration: p.estimatedDuration || null,
    lastUpdatedAt: epochToIso(p.lastUpdatedTime ?? p.createdAt),
    delivered: (p.status || "").toUpperCase() === "DELIVERED",
    progresses,
  };
}

/** 归一化单条商品明细（cart.groups[0].items[]）：把各次发货的分段平铺 */
function normalizeDetailItem(it: RawGroupItem): OrderDetailItem {
  const sku = it.sku ?? {};
  const img = sku.images?.[0];
  const shipping = Array.isArray(it.shipping) ? it.shipping : [];
  const segments = shipping.flatMap((s) =>
    (Array.isArray(s.packages) ? s.packages : []).map(normalizeSegment)
  );
  return {
    imageUrl: img?.thumbnail?.url || img?.full?.url || null,
    name: sku.name || "",
    brand: sku.brand || null,
    size: sku.size?.displayName || sku.size?.name || sku.size?.label || null,
    color: sku.color?.name || null,
    quantity: typeof it.quantity === "number" ? it.quantity : 1,
    segments,
  };
}

/** 把下游 /orders/{id} 响应归一化成 OrderDetail（商品固定取 groups[0]） */
function normalizeOrderDetail(o: RawOrderDetailResponse): OrderDetail {
  const group = o.cart?.groups?.[0];
  const rawItems = Array.isArray(group?.items) ? group!.items! : [];
  return {
    orderId: o.id ?? "",
    merchantOrderId: groupMerchantOrderId(o.cart),
    createdAt: epochToIso(o.placedAt),
    status: o.status ?? "",
    statusHuman: o.statusHuman || null,
    orderPriceCents: orderPriceCents(o.cart),
    actualAmountCents: actualAmountCents(o),
    items: rawItems.map(normalizeDetailItem),
  };
}

// ---------- 接口 ----------

/** GET /orders：分页 + 筛选，返回归一化后的订单列表 */
export async function getOrders(query: OrdersQuery = {}): Promise<OrdersPage> {
  const { page = 1, size = 20 } = query;
  const params: Record<string, string | number> = { page, size };
  if (query.search) params.search = query.search;
  if (query.merchantOrderId) params.merchantOrderId = query.merchantOrderId;
  if (query.status) params.status = query.status;
  if (query.from) params.from = query.from;
  if (query.to) params.to = query.to;

  const payload = await get<RawOrdersResponse>("/orders", {
    params,
    headers: { "X-Bxl-User-Agent": "BXL CloudStore AI" },
  });
  const rows = Array.isArray(payload?.orders) ? payload.orders : [];
  return {
    orders: rows.map(normalizeOrder),
    total:
      typeof payload?.total === "number" ? payload.total : rows.length,
    page,
    size,
  };
}

/** GET /orders/{orderId}：订单详情（供弹窗使用），携带自定义 header */
export async function getOrderDetail(orderId: string): Promise<OrderDetail> {
  const payload = await get<RawOrderDetailResponse>(
    `/orders/${encodeURIComponent(orderId)}`,
    { headers: { "X-Bxl-User-Agent": "BXL CloudStore AI" } }
  );
  return normalizeOrderDetail(payload ?? {});
}

/**
 * GET /orders/export：导出订单 CSV。
 * from/to 为 ISO instant（默认后端取最近 90 天）。返回 Blob 供前端触发下载。
 */
export async function exportOrdersCsv(params: {
  from?: string;
  to?: string;
} = {}): Promise<Blob> {
  const res = await request.get("/orders/export", {
    params,
    responseType: "blob",
  });
  // 响应拦截器已返回 response.data，这里即为 Blob
  return res as unknown as Blob;
}
